import {
    Collapse,
    Divider,
    LinearProgress,
    ListItemText,
    Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Modal, ModalContent, useDisclosure } from '@nextui-org/react'
import { buttonSearchCommonStyles } from '../common/styles'
import cn from 'classnames'
import qs from 'qs'
import React, { useContext, useEffect, useState } from 'react'
import { DropAlert } from '../drops/DropAlert'
import {
    get,
    getUnclaimedCraftResults,
    getWithdrawableItems,
} from '../helpers/Api'
import {
    setQueryStringWithoutPageReloadPush,
    sleep,
} from '../helpers/FormatLinks'
import FilterListIcon from '@mui/icons-material/FilterList'
import { getBoostAction } from '../helpers/WaxApi'
import LoadingIndicator from '../loadingindicator'
import { ScrollContext } from '../page/Page'
import { useSharedState } from '../waxplorer/Waxplorer'
import { CraftBalanceWithdrawalDialog } from './CraftBalanceWithdrawalDialog'
import CraftPreview from './CraftPreview'
import CraftSearchFilters from './CraftSearchFilters'
import CraftTopFilters from './CraftTopFilters'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import MainButton from '../common/util/input/MainButton'

export const CraftsList = (props) => {
    const { isOpen, onOpen, onOpenChange } = useDisclosure()
    const [open, setIsOpen] = useState(false)
    const toggleDrawer = () => {
        setIsOpen((prevState) => !prevState)
    }
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [unclaimedResultIds, setUnclaimedResultIds] = useState(null)
    const [isClaiming, setIsClaiming] = useState(false)

    const [withdrawableItems, setWithdrawableItems] = useState(null)
    const [isWithdrawing, setIsWithdrawing] = useState(null)

    const [error, setError] = useState(null)

    const claimAssets = async () => {
        setError(null)
        setIsClaiming(true)
        const actions = [getBoostAction(activeUser)]

        for (const resultId of unclaimedResultIds) {
            const data = {
                result_id: resultId,
                claimer: userName,
            }

            const action = {
                account: 'nfthivecraft',
                name: 'claim',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: data,
            }

            actions.push(action)
        }

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
        } catch (e) {
            setError(e.message)
            setIsClaiming(false)
        }
        await sleep(4000)
        fetchUnclaimedCraftResults()
        await sleep(1000)
        setIsClaiming(false)
    }

    const withdrawItems = async () => {
        setError(null)
        setIsWithdrawing(true)
        const actions = [getBoostAction(activeUser)]

        const data = {
            asset_ids: withdrawableItems,
            account: userName,
        }

        const action = {
            account: 'nfthivecraft',
            name: 'withditems',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: data,
        }

        actions.push(action)

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
        } catch (e) {
            setError(e.message)
            setIsWithdrawing(false)
        }
        await sleep(4000)
        fetchWithdrawableItems()
        await sleep(1000)
        setIsWithdrawing(false)
    }

    let values = qs.parse(
        window.location.search.substring(1, window.location.search.length),
    )

    const page = props['page']

    const getValue = (key, def) => {
        if (values[key]) return unescape(values[key])
        return def
    }

    const [offset, setOffset] = useState(getValue('offset', 0))
    const [orderBy, setOrderBy] = useState(getValue('order_by', 'date_desc'))
    const [market, setMarket] = useState(
        getValue('market', props['market'] ? props['market'] : 'nfthivecraft'),
    )
    const [searchCraftId, setSearchCraftId] = useState(
        getValue('craft_id', null),
    )
    const [searchAuthor, setSearchAuthor] = useState(
        props['collection'] ? props['collection'] : getValue('collection', '*'),
    )
    const authorFilter = !props['collection']

    const [searchVerified, setSearchVerified] = useState(
        getValue(
            'verified',
            searchAuthor && searchAuthor !== '*'
                ? 'all'
                : process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? 'all'
                : 'verified',
        ),
    )

    const [crafts, setCrafts] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [searchCnt, setSearchCnt] = useState(1)
    const [lastSearch, setLastSearch] = useState('')
    const displayCollectionSelector = props['displayCollectionSelector']
    const limit = 12

    const [balanceDialogOpen, setBalanceDialogOpen] = useState(false)

    const showFilters = props['showFilters']

    const parseCrafts = (res) => {
        if (offset > 0 && crafts) {
            setCrafts(crafts.concat(res))
        } else {
            setCrafts(res)
        }
        setIsLoading(false)
    }

    const getCrafts = async () => {
        get(
            `crafts?craft_id=${searchCraftId ? searchCraftId : ''}&offset=${
                offset ? offset : 0
            }&order_by=${orderBy ? orderBy : ''}&collection=${
                searchAuthor ? searchAuthor : ''
            }&verified=${searchVerified}&market=${
                market && market !== 'all' ? market : ''
            }&limit=${limit}`,
            'wax-api',
        ).then(parseCrafts)
    }

    const parseUnclaimedCraftResults = (res) => {
        const temp = []
        for (const result of res) {
            if (result.owner === userName) {
                temp.push(result.result_id)
            }
        }
        setUnclaimedResultIds(temp.length > 0 ? temp : null)
    }

    const fetchUnclaimedCraftResults = () => {
        getUnclaimedCraftResults(userName, state).then(
            parseUnclaimedCraftResults,
        )
    }

    const parseWithdrawableItems = (res) => {
        setWithdrawableItems(
            res !== null && res.assets && res.assets.length > 0
                ? res.assets
                : null,
        )
    }

    const fetchWithdrawableItems = () => {
        getWithdrawableItems(userName, state).then(parseWithdrawableItems)
    }

    useEffect(() => {
        if (userName) {
            fetchUnclaimedCraftResults()
            fetchWithdrawableItems()
        }
    }, [userName])

    useEffect(() => {
        setIsLoading(true)
        if (typeof window !== 'undefined') getCrafts()
    }, [searchCnt])

    const setOrder = (order) => {
        setVars(searchCraftId, searchAuthor, order, searchVerified, market, 0)
    }

    const setVars = (craftId, author, order, verified, market, offset = 0) => {
        const query = values

        if (craftId) query['craft_id'] = craftId
        else delete query['craft_id']
        if (author && author !== '*') query['collection'] = author
        else delete query['collection']
        if (order && order !== 'date_desc') query['order_by'] = order
        else delete query['order_by']
        if (offset) query['offset'] = offset
        else delete query['offset']
        if (verified !== 'verified') query['verified'] = verified
        else delete query['verified']
        if (market && market !== 'nfthivecraft') query['market'] = market
        else delete query['market']

        setSearchCraftId(craftId)
        setSearchAuthor(author)
        setOrderBy(order)
        setMarket(market)
        setOffset(offset)
        setSearchVerified(verified)

        const searchStr = qs.stringify(query)
        setSearchCnt(searchStr !== lastSearch ? searchCnt + 1 : searchCnt)
        setLastSearch(searchStr)

        setQueryStringWithoutPageReloadPush(qs.stringify(query))
    }

    const clearFilters = () => {
        setVars('', '*', 'date_desc', true, '', 0)
    }

    const { scrollManager, setScrollManager } = useContext(ScrollContext)

    const initScrollManager = async (
        triggered,
        useManager = true,
        scrollUp = false,
    ) => {
        setScrollManager({
            useManager: useManager,
            offset: 0,
            triggered: triggered,
            scrollUp: scrollUp,
        })
    }

    useEffect(() => {
        initScrollManager(false)
    }, [page])

    useEffect(() => {
        initScrollManager(false, !isLoading, false)
    }, [isLoading])

    const loadMore = async () => {
        if (
            !isLoading &&
            crafts &&
            crafts.length > 0 &&
            crafts.length % limit === 0
        ) {
            setIsLoading(true)
            setOffset(offset + limit)
            setVars(
                searchCraftId,
                searchAuthor,
                orderBy,
                searchVerified,
                market,
                parseInt(offset) + parseInt(limit),
            )
        }
    }

    useEffect(() => {
        if (scrollManager.triggered) {
            loadMore()
        }
    }, [scrollManager.triggered])

    return (
        <div className={cn('w-full flex flex-col sm:flex-row')}>
            <CraftBalanceWithdrawalDialog
                open={balanceDialogOpen}
                setOpen={setBalanceDialogOpen}
            />
            {showFilters && (
                <>
                    <Drawer
                        lockBackgroundScroll={true}
                        open={open}
                        onClose={toggleDrawer}
                        direction="left"
                        className="min-w-[330px] hidden md:block dark:!bg-zinc-900 bg-white p-4 overflow-y-auto !h-full"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <ListItemText>
                                <div className="text-lg font-semibold text-black dark:text-white">
                                    Filters
                                </div>
                            </ListItemText>
                            <Tooltip
                                showArrow={true}
                                key="right"
                                placement="right"
                                content="Close Filters"
                                color="foreground"
                                delay={0}
                                closeDelay={0}
                            >
                                <Button
                                    isIconOnly
                                    aria-label="Like"
                                    onClick={toggleDrawer}
                                    className={cn(
                                        'sm1:w-[36px] sm1:h-[36px] md1:w-[56px] md1:h-[56px] border-2',
                                        buttonSearchCommonStyles,
                                    )}
                                >
                                    <CloseIcon />
                                </Button>
                            </Tooltip>
                        </div>
                        <Divider />
                        <div className="mb-4 " />
                        <CraftSearchFilters
                            setVars={setVars}
                            searchCraftId={searchCraftId}
                            searchAuthor={searchAuthor}
                            orderBy={orderBy}
                            searchVerified={searchVerified}
                            market={market}
                            isLoading={!crafts}
                            clearFilters={clearFilters}
                            displayCollectionSelector={
                                displayCollectionSelector
                            }
                            authorFilter={authorFilter}
                        />
                    </Drawer>
                    <div className="block md:hidden ">
                        <Modal
                            isOpen={isOpen}
                            placement="bottom-center"
                            onOpenChange={onOpenChange}
                            height="auto"
                            scrollBehavior="inside"
                            classNames={{
                                base: 'max-w-[900px] overflow-y-auto InsideScroll  w-full !mt-24 block md:hidden',
                                body: '  ',
                            }}
                        >
                            <ModalContent>
                                {(onClose) => (
                                    <div className="p-2 ">
                                        <div className="flex items-center justify-between ">
                                            <p className="ml-2 text-lg text-semibold ">
                                                Filters
                                            </p>
                                        </div>
                                        <Divider />
                                        <CraftSearchFilters
                                            setVars={setVars}
                                            searchCraftId={searchCraftId}
                                            searchAuthor={searchAuthor}
                                            orderBy={orderBy}
                                            searchVerified={searchVerified}
                                            market={market}
                                            isLoading={!crafts}
                                            clearFilters={clearFilters}
                                            displayCollectionSelector={
                                                displayCollectionSelector
                                            }
                                            authorFilter={authorFilter}
                                        />
                                    </div>
                                )}
                            </ModalContent>
                        </Modal>
                    </div>
                </>
            )}
            <div className={cn('px-5 w-full mx-auto mt-10 sm:mt-0')}>
                <div
                    className={cn(
                        'flex flex-col sm:flex-row justify-center sm:justify-between mb-5',
                    )}
                >
                    <div className="flex items-center justify-between w-full">
                        <div>
                            <Button
                                isIconOnly
                                aria-label="Like"
                                onClick={() => setIsOpen(!open)}
                                className={cn(
                                    'w-[58px] md:block hidden h-[58px] mr-2 border-2',
                                    buttonSearchCommonStyles,
                                )}
                            >
                                <FilterListIcon />
                            </Button>
                            <Button
                                isIconOnly
                                aria-label="Like"
                                onPress={onOpen}
                                className={cn(
                                    'w-[58px] block mt-2 md:hidden h-[58px] mr-2 border-2',
                                    buttonSearchCommonStyles,
                                )}
                            >
                                <FilterListIcon />
                            </Button>
                        </div>
                        <div className="w-full pt-2 my-2 sm:my-auto sm:w-2/5">
                            <CraftTopFilters
                                disabled={isLoading}
                                setOrder={setOrder}
                                orderBy={orderBy}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-start">
                    <MainButton
                        variant="solid"
                        color="primary"
                        onClick={() => setBalanceDialogOpen(true)}
                        className="uppercase "
                    >
                        Check your Crafting Balance
                    </MainButton>
                </div>
                <DropAlert
                    condition={unclaimedResultIds !== null}
                    severity="info"
                    title="You have unclaimed crafting results!"
                >
                    <div>
                        We found some of your unclaimed NFTs that are currently
                        owned by the contract, but which belong to you. This can
                        happen if you don{"'"}t claim the results of a craft
                        with preminted outcomes immediately. You can withdraw
                        these NFTs into your wallet by clicking this button:
                    </div>
                    <Collapse in={isClaiming}>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </Collapse>
                    <div className="flex justify-center mt-5">
                        <Button
                            variant="outlined"
                            disabled={isClaiming}
                            onClick={() => claimAssets()}
                        >
                            Claim NFTs
                        </Button>
                    </div>
                </DropAlert>
                <DropAlert
                    condition={withdrawableItems !== null}
                    severity="info"
                    title="You have withdrawable NFTs!"
                >
                    <div>
                        We found some of your NFTs that are currently owned by
                        the contract, but which belong to you. This can happen
                        if you used an NFT during crafting which was supposed to
                        be returned to you in the process. You can withdraw
                        these NFTs into your wallet by clicking this button:
                    </div>
                    <Collapse in={isWithdrawing}>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </Collapse>
                    <div className="flex justify-center mt-5">
                        <Button
                            variant="outlined"
                            disabled={isWithdrawing}
                            onClick={() => withdrawItems()}
                        >
                            Withdraw NFTs
                        </Button>
                    </div>
                </DropAlert>
                <DropAlert
                    severity="error"
                    condition={error !== null}
                    title="An error occured!"
                >
                    {error}
                </DropAlert>
                <div
                    className={
                        'mt-8 grid gap-4 grid-cols-1 xsse:grid-cols-1 xs1:grid-cols-1 sm1:grid-cols-2 md:grid-cols-2 md1:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 4xl:grid-cols-6 text-center'
                    }
                >
                    {crafts &&
                        !('error' in crafts) &&
                        crafts.map((craft, index) => (
                            <CraftPreview
                                {...props}
                                index={index}
                                craft={craft}
                                key={index}
                            />
                        ))}
                </div>
                {isLoading ? <LoadingIndicator /> : ''}
            </div>
        </div>
    )
}
